import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const SuccessStoriesPreview = () => {
  const stories = [
    {
      title: 'The Coffee Machine Crisis',
      category: 'Problem Escalation',
      excerpt:
        'How we transformed a minor office coffee machine malfunction into a company-wide emergency requiring immediate executive attention and a 47-page incident report.',
      image: 'https://public.readdy.ai/ai/img_res/2854078b189808a49d9496d8ff3c561a.jpg',
      readTime: '8 min read',
    },
    {
      title: 'The Email Signature Dilemma',
      category: 'Problem Identification',
      excerpt:
        'A comprehensive analysis of how one employee\'s inconsistent email signature font revealed deeper organizational problems that we documented extensively without addressing.',
      image: 'https://readdy.ai/api/search-image?query=person%20working%20at%20laptop%20with%20dramatic%20sunset%20through%20window%20silhouette%20golden%20hour%20lighting%20professional%20workspace%20contemplative%20mood%20warm%20tones%20cinematic%20composition%20business%20environment&width=800&height=600&seq=story-email-005&orientation=landscape',
      readTime: '12 min read',
    },
    {
      title: 'The Parking Spot Predicament',
      category: 'Problem Awareness',
      excerpt:
        'Our team spent six months helping a client become fully aware that their parking spot was 15 feet further from the entrance than their colleague\'s spot. Awareness achieved.',
      image: 'https://readdy.ai/api/search-image?query=empty%20parking%20lot%20at%20golden%20sunset%20with%20dramatic%20sky%20and%20long%20shadows%20urban%20landscape%20moody%20atmosphere%20cinematic%20lighting%20professional%20photography%20warm%20orange%20and%20purple%20tones%20contemplative%20scene&width=800&height=600&seq=story-parking-006&orientation=landscape',
      readTime: '6 min read',
    },
  ];

  return (
    <section className="relative py-32 bg-charcoal overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <span className="text-gold text-sm font-semibold tracking-widest uppercase mb-4 block">
            Case Studies
          </span>
          <h2 className="text-5xl md:text-6xl font-black text-white mb-6">
            Problem Recognition Success Stories
          </h2>
          <p className="text-xl text-light-gray max-w-2xl mx-auto">
            Real examples of how we've helped clients discover problems they never knew they had
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {stories.map((story, index) => (
            <motion.article
              key={index}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="group"
            >
              <Link to="/financial-success" className="block">
                <div className="bg-dark-gray rounded-2xl overflow-hidden border border-transparent hover:border-gold/30 transition-all duration-500">
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={story.image}
                      alt={story.title}
                      className="w-full h-full object-cover object-center group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="bg-gold text-black px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider">
                        {story.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-8">
                    <h3 className="text-2xl font-bold text-white mb-3 group-hover:text-gold transition-colors duration-300">
                      {story.title}
                    </h3>
                    <p className="text-light-gray text-base leading-relaxed mb-4">
                      {story.excerpt}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-light-gray/70">
                        {story.readTime}
                      </span>
                      <span className="text-gold text-sm font-semibold group-hover:gap-2 inline-flex items-center gap-1 transition-all">
                        Read More
                        <i className="ri-arrow-right-line"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            </motion.article>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="text-center mt-16"
        >
          <Link
            to="/financial-success"
            className="inline-flex items-center gap-3 border-2 border-gold text-gold px-8 py-4 rounded-full text-base font-bold hover:bg-gold hover:text-black transition-all duration-300 whitespace-nowrap"
          >
            View All Case Studies
            <i className="ri-arrow-right-line text-xl"></i>
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default SuccessStoriesPreview;